import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Editor from '@monaco-editor/react';
import { toast } from 'react-toastify';

import {
  Avatar,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  Modal,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material';

import LoadingButton from '../../../../components/buttons/Button';
import TopBar from '../../../../components/utils/TopBar';

import {
  GetEmailTemplateBIZnotifications
} from '../../../../redux/actions/business/notifications/EmailTemplatesActions';

import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

import style from "../../../../global.scss";

const Data = {
  templateId: "",
  htmlTemplate: "",
  subject: "",
  selectionMode: "all", // "all", "custom", "exclude"
  selectedUsers: [],
  excludedUsers: []
};

// Mock user data for visualization
const mockUsers = [
  { id: 1, name: "John Doe", email: "john@example.com" },
  { id: 2, name: "Jane Smith", email: "jane@example.com" },
  { id: 3, name: "Bob Johnson", email: "bob@example.com" },
  { id: 4, name: "Alice Williams", email: "alice@example.com" },
  { id: 5, name: "Charlie Brown", email: "charlie@example.com" },
];

const EmailBroadcastPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.notifications"]);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [formData, setFormData] = useState(Data);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const htmlEditorRef = useRef(null);

  useEffect(() => {
    try {
      if(state.emailTemplateDetails && state.emailTemplateDetails.id) {
        setFormData({
          ...formData,
          htmlTemplate: state.emailTemplateDetails.template || "",
          subject: state.emailTemplateDetails.subject || ""
        });
      }
    } catch (err){
      console.log(err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  function handleHtmlEditorDidMount(editor, monaco) {
    htmlEditorRef.current = editor;
    editor.getAction('editor.action.formatDocument').run();
  }

  const handleGetTemplate = () => {
    if(!formData.templateId || formData.templateId.trim() === "") {
      toast.error("Please enter a template ID");
      return;
    }
    dispatch(GetEmailTemplateBIZnotifications(formData.templateId, setLoadingTemplate));
  };

  const handleReset = () => {
    setFormData(Data);
  };

  const handleSendBroadcast = () => {
    if(!formData.subject || formData.subject.trim() === "") {
      toast.error("Please enter a subject");
      return;
    }
    if(!formData.htmlTemplate || formData.htmlTemplate.trim() === "") {
      toast.error("Please load a template or enter HTML");
      return;
    }
    if(formData.selectionMode === "custom" && formData.selectedUsers.length === 0) {
      toast.error("Please select at least one user");
      return;
    }

    // TODO: Dispatch send broadcast email action when backend endpoint is ready
    setSendingEmail(true);
    toast.info("Broadcast email functionality - backend integration pending");
    setTimeout(() => {
      setSendingEmail(false);
    }, 1000);
  };

  const handleUserSelection = (user) => {
    if(formData.selectionMode === "custom") {
      const isSelected = formData.selectedUsers.find(u => u.id === user.id);
      if(isSelected) {
        setFormData({
          ...formData,
          selectedUsers: formData.selectedUsers.filter(u => u.id !== user.id)
        });
      } else {
        setFormData({
          ...formData,
          selectedUsers: [...formData.selectedUsers, user]
        });
      }
    } else if(formData.selectionMode === "exclude") {
      const isExcluded = formData.excludedUsers.find(u => u.id === user.id);
      if(isExcluded) {
        setFormData({
          ...formData,
          excludedUsers: formData.excludedUsers.filter(u => u.id !== user.id)
        });
      } else {
        setFormData({
          ...formData,
          excludedUsers: [...formData.excludedUsers, user]
        });
      }
    }
  };

  const handleRemoveUser = (userId) => {
    if(formData.selectionMode === "custom") {
      setFormData({
        ...formData,
        selectedUsers: formData.selectedUsers.filter(u => u.id !== userId)
      });
    } else if(formData.selectionMode === "exclude") {
      setFormData({
        ...formData,
        excludedUsers: formData.excludedUsers.filter(u => u.id !== userId)
      });
    }
  };

  const getRecipientCount = () => {
    if(formData.selectionMode === "all") return "All Users";
    if(formData.selectionMode === "custom") return `${formData.selectedUsers.length} selected`;
    if(formData.selectionMode === "exclude") return `All except ${formData.excludedUsers.length}`;
    return "0";
  };

  const filteredMockUsers = mockUsers.filter(user => 
    user.name.toLowerCase().includes(userSearch.toLowerCase()) ||
    user.email.toLowerCase().includes(userSearch.toLowerCase())
  );

  return (
    <Box>
      <TopBar
        title={"Email Broadcast"}
        />

      <Box>
        
        {/* Template ID Search Section */}
        <Box className="block" sx={{ padding: "20px", marginBottom: "15px" }}>
          <Box 
            className="flexCenter_Row"
            gap={"15px"}>
            <TextField
              fullWidth
              label="Template ID"
              name="templateId"
              value={formData.templateId}
              onChange={(e) => setFormData({ ...formData, templateId: e.target.value })}
              disabled={loadingTemplate}
              placeholder="Enter template ID to fetch template"
            />
            <LoadingButton
              variant='contained'
              loading={loadingTemplate}
              onClick={handleGetTemplate}
              sx={{ minWidth: "120px" }}>
              Search
            </LoadingButton>
          </Box>
        </Box>

        {/* HTML Template Editor and Preview Section */}
        <Box className="block" sx={{ padding: "20px", marginBottom: "15px" }}>
          <Box 
            className="flexCenter_Row"
            gap={"15px"}
            sx={{ alignItems: "flex-start" }}>
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <Typography variant='subtitle2' mb={"8px"} sx={{ color: style["primary"] }}>
                Template (HTML)
              </Typography>
              <Editor
                height="450px"
                language={"html"}
                value={formData.htmlTemplate || "<!-- Enter HTML template here -->"}
                onChange={(value) => setFormData({ ...formData, htmlTemplate: value })}
                theme='vs-dark'
                options={{
                  wordWrap: true,
                  formatOnPaste: true,
                  formatOnType: true,
                  scrollBeyondLastLine: false,
                  minimap: { enabled: false }
                }}
                onMount={handleHtmlEditorDidMount}
              />
            </Box>
            
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <Typography variant='subtitle2' mb={"8px"} sx={{ color: style["primary"] }}>
                Email Preview
              </Typography>
              <Box
                sx={{
                  height: "450px",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "4px",
                  overflow: "auto",
                  backgroundColor: "#ffffff"
                }}>
                <iframe
                  title="Email Preview"
                  srcDoc={`
                    <!DOCTYPE html>
                    <html>
                      <head>
                        <meta charset="utf-8">
                        <meta name="viewport" content="width=device-width, initial-scale=1.0">
                        <style>
                          body {
                            margin: 0;
                            padding: 16px;
                            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
                            font-size: 14px;
                            line-height: 1.6;
                            color: #000000;
                            background-color: #ffffff;
                          }
                          * {
                            box-sizing: border-box;
                          }
                        </style>
                      </head>
                      <body>
                        ${formData.htmlTemplate || '<p style="color: #666; font-style: italic;">Your email preview will appear here...</p>'}
                      </body>
                    </html>
                  `}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none"
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Subject and Recipients Section */}
        <Box className="block" sx={{ padding: "20px" }}>
          <Box mb={"15px"}>
            <TextField
              fullWidth
              label="Subject"
              name="subject"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              disabled={sendingEmail}
              placeholder="Enter email subject"
            />
          </Box>

          {/* User Selection Mode */}
          <Box mb={"15px"}>
            <Typography variant='subtitle2' mb={"8px"} sx={{ color: style["primary"] }}>
              Recipient Selection
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup
                row
                value={formData.selectionMode}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  selectionMode: e.target.value,
                  selectedUsers: [],
                  excludedUsers: []
                })}>
                <FormControlLabel value="all" control={<Radio />} label="All Users" />
                <FormControlLabel value="custom" control={<Radio />} label="Custom Selection" />
                <FormControlLabel value="exclude" control={<Radio />} label="All Except Selected" />
              </RadioGroup>
            </FormControl>
          </Box>

          {/* User Selection Button and Display */}
          {(formData.selectionMode === "custom" || formData.selectionMode === "exclude") && (
            <Box>
              <Box 
                className="flexCenter_Row"
                gap={"15px"}
                mb={"15px"}>
                <Button
                  variant="outlined"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setUserModalOpen(true)}
                  fullWidth>
                  {formData.selectionMode === "custom" ? "Select Users" : "Exclude Users"}
                </Button>
                <Box 
                  sx={{ 
                    minWidth: "150px",
                    padding: "8px 12px",
                    backgroundColor: "rgba(44, 45, 47, 0.64)",
                    borderRadius: "8px",
                    textAlign: "center"
                  }}>
                  <Typography variant='body2' sx={{ color: style["primary"] }}>
                    {getRecipientCount()}
                  </Typography>
                </Box>
              </Box>

              {/* Display Selected/Excluded Users */}
              {((formData.selectionMode === "custom" && formData.selectedUsers.length > 0) ||
                (formData.selectionMode === "exclude" && formData.excludedUsers.length > 0)) && (
                <Box 
                  className="flexCenter_Row"
                  gap={"8px"}
                  mb={"15px"}
                  sx={{ flexWrap: "wrap" }}>
                  {(formData.selectionMode === "custom" ? formData.selectedUsers : formData.excludedUsers).map(user => (
                    <Chip
                      key={user.id}
                      avatar={<Avatar>{user.name.charAt(0)}</Avatar>}
                      label={`${user.name} (${user.email})`}
                      onDelete={() => handleRemoveUser(user.id)}
                      deleteIcon={<CloseIcon />}
                      sx={{ backgroundColor: "rgba(44, 45, 47, 0.64)" }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* Recipients Summary for "All Users" mode */}
          {formData.selectionMode === "all" && (
            <Box 
              sx={{ 
                padding: "12px",
                backgroundColor: "rgba(44, 45, 47, 0.64)",
                borderRadius: "8px",
                marginBottom: "15px"
              }}>
              <Typography variant='body2' sx={{ color: style["primary"] }}>
                📧 This email will be sent to all users in the database
              </Typography>
            </Box>
          )}

          {/* Action Buttons */}
          <Box className="flexCenterFERow" sx={{ gap: "15px" }}>
            <Button 
              variant='outlined'
              onClick={handleReset}
              disabled={sendingEmail}>
              Reset
            </Button>
            <LoadingButton
              variant='contained'
              loading={sendingEmail}
              disabled={
                !(formData?.subject?.trim() !== "" && formData?.htmlTemplate?.trim() !== "")
              }
              onClick={handleSendBroadcast}>
              Send Broadcast
            </LoadingButton>
          </Box>
        </Box>

      </Box>

      {/* User Selection Modal - MOCKUP */}
      <Modal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        aria-labelledby="user-selection-modal">
        <Box className="flexCenterCenterRow"
          width={"100vw"} height={"100vh"}>
          <Box maxWidth={"md"} className="modal" sx={{ width: "600px" }}>
            <Box sx={{ padding: "20px" }}>
              <Typography variant='h6' sx={{ color: style["primary"] }} mb={"15px"}>
                {formData.selectionMode === "custom" ? "Select Users" : "Exclude Users"}
              </Typography>
              
              {/* Search Bar */}
              <TextField
                fullWidth
                label="Search Users"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search by name or email"
                sx={{ marginBottom: "15px" }}
              />

              {/* Mock User List */}
              <Box 
                sx={{ 
                  maxHeight: "400px", 
                  overflowY: "auto",
                  marginBottom: "15px"
                }}>
                <Typography variant='caption' sx={{ color: style["secondary_light"], marginBottom: "10px", display: "block" }}>
                  🎨 MOCKUP: This will fetch real users from database
                </Typography>
                {filteredMockUsers.map(user => {
                  const isSelected = formData.selectionMode === "custom" 
                    ? formData.selectedUsers.find(u => u.id === user.id)
                    : formData.excludedUsers.find(u => u.id === user.id);
                  
                  return (
                    <Box
                      key={user.id}
                      className="flexCenter_Row"
                      gap={"15px"}
                      sx={{
                        padding: "12px",
                        marginBottom: "8px",
                        backgroundColor: isSelected ? "rgba(76, 175, 80, 0.2)" : "rgba(44, 45, 47, 0.64)",
                        borderRadius: "8px",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: isSelected ? "rgba(76, 175, 80, 0.3)" : "rgba(44, 45, 47, 0.8)"
                        }
                      }}
                      onClick={() => handleUserSelection(user)}>
                      <Avatar>{user.name.charAt(0)}</Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant='body2'>{user.name}</Typography>
                        <Typography variant='caption' sx={{ color: style["secondary_light"] }}>
                          {user.email}
                        </Typography>
                      </Box>
                      {isSelected && (
                        <Chip 
                          label={formData.selectionMode === "custom" ? "Selected" : "Excluded"}
                          size="small"
                          color="success"
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>

              {/* Modal Actions */}
              <Box className="flexCenterFERow" sx={{ gap: "15px" }}>
                <Button 
                  variant='outlined'
                  onClick={() => setUserModalOpen(false)}>
                  Cancel
                </Button>
                <Button
                  variant='contained'
                  onClick={() => setUserModalOpen(false)}>
                  Confirm
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>

    </Box>
  );
}

export default EmailBroadcastPage;